import { useState } from 'react';
import { TextField, Button, Stack, Box, Typography, TextFieldProps } from '@mui/material';
import { MapResponsive } from 'components-new';
import { toast } from 'react-toastify';
import { LeadCategory } from '@/_models/TourLead';
import { fetchData } from '@/components/util';

const Input = (props: TextFieldProps) => (
  <TextField
    {...props}
    sx={{
      ...props.sx,
      '& label': {
        color: 'gray',
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: 'gray',
        },
      },
      ...(!props.multiline && {
        '& .MuiInputBase-root , input': {
          height: '50px',
          lineHeight: '50px',
        },
      }),
    }}
    fullWidth
  />
);

type Props = {
  tour: any;
  isDesktop: boolean;
};

export default function ContactAndMap({ tour, isDesktop }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const validatePhone = (value) => {
    const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
    return regex.test(value);
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setMessage('');
    setSubmitted(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!validatePhone(phone)) {
      toast.error('Please enter a valid phone number');
    } else {
      const inputs = {
        name,
        email,
        phone,
        message,
        category: LeadCategory.AssertTouchForm,
        tourId: tour?.id,
        userId: tour?.userId,
        link: window.location.href,
        tour: tour?.title,
        clientId: tour?.client?.id,
      };

      const { tourEmail, client } = tour;

      const to = (tourEmail && tourEmail.assertGetInTouch) || (client && client.email);

      try {
        if (!to) toast.error('No email to send to');

        await fetchData('public/sendEmail', 'POST', {
          to,
          params: inputs,
        });
        toast.success('E-Mail sent');
        await fetchData('public/setTourLead', 'POST', inputs);

        resetForm();
      } catch (err) {
        console.error(err.message);
      }
    }
  };

  return (
    <Stack
      id="contact"
      spacing={3}
      direction={{
        xs: 'column-reverse',
        md: 'row',
      }}
      justifyContent={{ md: 'space-between' }}
    >
      <Box
        sx={{ position: 'relative', background: 'white', borderRadius: '5px', padding: 2 }}
        minWidth={{
          xs: '100%',
          md: '50%',
        }}
      >
        <Stack>
          <Typography variant="h4">{tour.translation?.contact || 'Contact us'}</Typography>
          <Typography variant="body1" sx={{ color: 'gray', mb: 2 }}>
            {tour.translation?.getInTouch || 'Get in touch with us today!'}
          </Typography>
        </Stack>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2} alignItems="flex-start">
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <Input type="email" label="Email" value={email} required onChange={(e) => setEmail(e.target.value)} />
            <Input label="Phone" value={phone} error={submitted && !validatePhone(phone)} required onChange={(e) => setPhone(e.target.value)} />
            <Input multiline rows={4} label="Message" value={message} onChange={(e) => setMessage(e.target.value)} required />
            <Button
              fullWidth={!isDesktop}
              size="large"
              type="submit"
              variant="contained"
              sx={{
                mx: { xs: 'auto !important', md: 'unset !important' },
                color: 'white',
              }}
            >
              {tour.translation?.submit || 'Submit'}
            </Button>
          </Stack>
        </form>
      </Box>

      <Box flexGrow={1}>
        <MapResponsive ratio="1/1" address={tour.address} />
      </Box>
    </Stack>
  );
}
